import { RootState } from '@AppState/redux-store'
import { AuthScopes, FeatureFlag } from '@Generated/back-office-client/src'
import { Container, Flex, Heading, SegmentedControl, Tabs, Text } from '@radix-ui/themes'
import { useSelector } from 'react-redux'
import { ScanHealthTab } from './scan-health-tab'
import { SpinHealthTab } from './spin-health-tab'
import { DEFAULT_WINDOW_HOURS, HealthSearchParam, HealthTab, WINDOW_HOURS_OPTIONS } from './types'
import { useHealthSearchParams } from './use-health-search-params'

export const SystemHealth = () => {
  const { activeCustomerMetadata, currentUser } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'
  const { searchParams, setParam } = useHealthSearchParams()

  let windowHours = DEFAULT_WINDOW_HOURS
  const requestedWindowHours = Number(searchParams.get(HealthSearchParam.WINDOW))
  if (WINDOW_HOURS_OPTIONS.includes(requestedWindowHours)) {
    windowHours = requestedWindowHours
  }

  const canSeeSpins =
    !!currentUser?.scopes.includes(AuthScopes.readspin_to_win) &&
    !!currentUser?.enabledFeatures.includes(FeatureFlag.spin_to_win)
  const canSeeScans = !!currentUser?.scopes.includes(AuthScopes.readplayer_scans)

  const visibleTabs: HealthTab[] = []
  if (canSeeSpins) {
    visibleTabs.push(HealthTab.SPINS)
  }
  if (canSeeScans) {
    visibleTabs.push(HealthTab.SCANS)
  }
  let tab = visibleTabs[0]
  const requestedTab = searchParams.get(HealthSearchParam.TAB) as HealthTab | null
  if (requestedTab && visibleTabs.includes(requestedTab)) {
    tab = requestedTab
  }

  return (
    <Container>
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Heading>System Health</Heading>
          <SegmentedControl.Root
            value={`${windowHours}`}
            onValueChange={(value) => setParam(HealthSearchParam.WINDOW, value)}>
            {WINDOW_HOURS_OPTIONS.map((hours) => (
              <SegmentedControl.Item key={hours} value={`${hours}`}>
                {hours}h
              </SegmentedControl.Item>
            ))}
          </SegmentedControl.Root>
        </Flex>

        {tab ? (
          <Tabs.Root value={tab} onValueChange={(value) => setParam(HealthSearchParam.TAB, value)}>
            <Tabs.List>
              {canSeeSpins ? <Tabs.Trigger value={HealthTab.SPINS}>Spins</Tabs.Trigger> : null}
              {canSeeScans ? <Tabs.Trigger value={HealthTab.SCANS}>Scans</Tabs.Trigger> : null}
            </Tabs.List>
            {canSeeSpins ? (
              <Tabs.Content value={HealthTab.SPINS}>
                <SpinHealthTab windowHours={windowHours} timezone={timezone} />
              </Tabs.Content>
            ) : null}
            {canSeeScans ? (
              <Tabs.Content value={HealthTab.SCANS}>
                <ScanHealthTab windowHours={windowHours} timezone={timezone} />
              </Tabs.Content>
            ) : null}
          </Tabs.Root>
        ) : (
          <Text>Nothing to show for your permissions</Text>
        )}
      </Flex>
    </Container>
  )
}
